import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Empty,
  Form,
  Input,
  message,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  DownloadOutlined,
  FilePdfOutlined,
  HistoryOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import { DriverContext } from "../context/DriverContext";
import {
  API_FleetDriver_ID_download_edoc_OT,
  API_FleetDriver_ID_search_cards_OT,
  API_REQUEST_HEADERS_BASEURL,
} from "../utils/Constants";
import TransportOrderDetailModal from "../components/transportOrderDetailModal/TransportOrderDetailModal";
import "./OrderHistory.css";

const ORDER_STATUSES = ["ARCHIVED", "FINALIZED", "ACTIVE", "ASSIGNED", "PENDING"];
const DATE_FIELDS = ["archivedAt", "finalizedAt", "activationAt", "createdAt"];
const PAGE_SIZE_OPTIONS = [25, 50, 100];

function toIsoDate(date) {
  if (!date || typeof date.format !== "function") return "";
  return date.format("YYYY-MM-DD");
}

function defaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 90);
  return [
    `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`,
    `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`,
  ];
}

function formatDate(value, locale) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function buildDownloadName(order) {
  const ref = String(
    order?.internalReference
      || order?.customerReference
      || order?.transportOrderRef
      || order?.transportOrderId
      || "order"
  )
    .replace(/[^a-z0-9_-]+/gi, "_")
    .replace(/^_+|_+$/g, "");
  return `${ref || "order"}-edoc.pdf`;
}

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

export default function OrderHistory() {
  const { getFleetID } = useContext(AuthContext);
  const { driversDetails } = useContext(DriverContext);
  const fleetId = getFleetID();
  const { t, i18n } = useTranslation();
  const locale = String(i18n?.resolvedLanguage || i18n?.language || "en").replace("_", "-");
  const [form] = Form.useForm();
  const [orders, setOrders] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingOrderIds, setDownloadingOrderIds] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [cursorStack, setCursorStack] = useState([]);
  const [lastQuery, setLastQuery] = useState(null);
  const isBatchDownloading = downloadingOrderIds.length > 0;

  const driverOptions = useMemo(
    () =>
      (driversDetails || [])
        .map((driver) => ({
          value: driver.id,
          label: driver.name || driver.phoneNumber || driver.id,
        }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [driversDetails]
  );

  const buildParams = (values, cursor = "") => {
    const [from, to] = values.dateRange || [];
    const params = {
      statuses: (values.statuses || ["ARCHIVED"]).join(","),
      dateField: values.dateField || "archivedAt",
      from: toIsoDate(from) || values.from || "",
      to: toIsoDate(to) || values.to || "",
      driverId: values.driverId || "",
      q: String(values.q || "").trim(),
      limit: values.limit || 50,
      cursor,
    };
    if (values.eDocOnly) params.eDocOnly = "true";
    return Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== "" && value !== undefined && value !== null)
    );
  };

  const fetchOrders = async (params, options = {}) => {
    if (!fleetId) return;
    setLoading(true);
    try {
      const endpoint = API_FleetDriver_ID_search_cards_OT.replace("{fleetId}", fleetId);
      const response = await API_REQUEST_HEADERS_BASEURL.get(endpoint, { params });
      setOrders(response.data?.items || []);
      setNextCursor(response.data?.nextCursor || null);
      setSelectedRowKeys([]);
      if (!options.keepLastQuery) {
        setLastQuery(params);
      }
    } catch (error) {
      console.error("Error loading order history:", error);
      setOrders([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    const values = form.getFieldsValue();
    const params = buildParams(values);
    setCursorStack([]);
    await fetchOrders(params);
  };

  const handleNextPage = async () => {
    if (!nextCursor || !lastQuery) return;
    setCursorStack((current) => [...current, lastQuery.cursor || ""]);
    const params = { ...lastQuery, cursor: nextCursor };
    await fetchOrders(params);
  };

  const handlePreviousPage = async () => {
    if (!lastQuery || cursorStack.length === 0) return;
    const previousCursor = cursorStack[cursorStack.length - 1] || "";
    setCursorStack((current) => current.slice(0, -1));
    const params = { ...lastQuery, cursor: previousCursor };
    await fetchOrders(params);
  };

  const downloadPdf = async (order) => {
    if (!fleetId || !order?.transportOrderId) return false;
    const orderId = order.transportOrderId;
    setDownloadingOrderIds((current) => [...new Set([...current, orderId])]);
    try {
      const endpoint = API_FleetDriver_ID_download_edoc_OT
        .replace("{fleetId}", fleetId)
        .replace("{fleetOT}", orderId);
      const response = await API_REQUEST_HEADERS_BASEURL.get(endpoint, {
        responseType: "blob",
        headers: { Accept: "application/pdf" },
      });
      saveBlob(response.data, buildDownloadName(order));
      return true;
    } catch (error) {
      console.error("Error downloading order PDF:", error);
      message.error(
        t("orderHistory.downloadError", {
          defaultValue: "Could not download the selected PDF.",
        })
      );
      return false;
    } finally {
      setDownloadingOrderIds((current) => current.filter((id) => id !== orderId));
    }
  };

  const handleBatchDownload = async () => {
    const selectedOrders = orders.filter((order) => selectedRowKeys.includes(order.transportOrderId));
    const downloadableOrders = selectedOrders.filter((order) => order.viewPdfUrl).slice(0, 50);
    let successCount = 0;
    for (const order of downloadableOrders) {
      // Sequential downloads avoid popup blockers and keep browser download prompts predictable.
      const didDownload = await downloadPdf(order);
      if (didDownload) successCount += 1;
    }
    if (successCount > 1) {
      message.success(
        t("orderHistory.downloadStarted", {
          defaultValue: "{{count}} PDF downloads started.",
          count: successCount,
        })
      );
    }
  };

  useEffect(() => {
    const [from, to] = defaultDateRange();
    form.setFieldsValue({
      statuses: ["ARCHIVED"],
      dateField: "archivedAt",
      from,
      to,
      limit: 50,
      eDocOnly: false,
    });
    const params = buildParams({
      statuses: ["ARCHIVED"],
      dateField: "archivedAt",
      from,
      to,
      limit: 50,
      eDocOnly: false,
    });
    fetchOrders(params);
  }, [fleetId]);

  const columns = [
    {
      title: t("orderDetail.internalReference", { defaultValue: "Internal reference" }),
      dataIndex: "internalReference",
      key: "internalReference",
      render: (value, order) => (
        <Button type="link" className="order-history-ref" onClick={() => setSelectedOrder(order)}>
          {value || order.transportOrderId}
        </Button>
      ),
    },
    {
      title: t("orderDetail.customerReference", { defaultValue: "Customer reference" }),
      dataIndex: "customerReference",
      key: "customerReference",
      render: (value, order) => value || order.transportOrderRef || "-",
    },
    {
      title: t("orderHistory.status", { defaultValue: "Status" }),
      dataIndex: "status",
      key: "status",
      render: (value) => <Tag>{t(`transportManagement.${String(value).toLowerCase()}`, { defaultValue: value })}</Tag>,
    },
    {
      title: t("orderHistory.driver", { defaultValue: "Driver" }),
      dataIndex: "name",
      key: "name",
      render: (value, order) => value || order.userId || "-",
    },
    {
      title: t("orderHistory.truck", { defaultValue: "Truck" }),
      dataIndex: "truck",
      key: "truck",
      render: (truck) => [truck?.headId, truck?.trailerId].filter(Boolean).join(" / ") || "-",
    },
    {
      title: t("orderHistory.route", { defaultValue: "Route" }),
      key: "route",
      render: (_, order) => [order.pickupName, order.deliveryName].filter(Boolean).join(" -> ") || "-",
    },
    {
      title: t("orderHistory.dates", { defaultValue: "Dates" }),
      key: "dates",
      render: (_, order) =>
        [
          order.archivedAt
            ? `${t("orderHistory.archived", { defaultValue: "Archived" })}: ${formatDate(order.archivedAt, locale)}`
            : "",
          order.finalizedAt
            ? `${t("orderHistory.finalized", { defaultValue: "Finalized" })}: ${formatDate(order.finalizedAt, locale)}`
            : "",
          order.activationAt
            ? `${t("orderHistory.activation", { defaultValue: "Activation" })}: ${formatDate(order.activationAt, locale)}`
            : "",
        ]
          .filter(Boolean)
          .join(" · ") || "-",
    },
    {
      title: t("orderHistory.edoc", { defaultValue: "eDoc" }),
      key: "edoc",
      render: (_, order) =>
        order.hasEDoc ? (
          <Tag color={order.edocStatusUnavailable ? 'orange' : 'green'}>{order.edocStatus || t("feedback.unavailable")}</Tag>
        ) : (
          <Tag>{t("orderHistory.none", { defaultValue: "None" })}</Tag>
        ),
    },
    {
      title: t("orderHistory.actions", { defaultValue: "Actions" }),
      key: "actions",
      render: (_, order) => (
        <Space>
          <Button size="small" onClick={() => setSelectedOrder(order)}>
            {t("orderHistory.details", { defaultValue: "Details" })}
          </Button>
          <Button
            size="small"
            icon={<FilePdfOutlined />}
            disabled={!order.viewPdfUrl || isBatchDownloading}
            loading={downloadingOrderIds.includes(order.transportOrderId)}
            onClick={() => downloadPdf(order)}
          >
            {t("orderHistory.pdf", { defaultValue: "PDF" })}
          </Button>
        </Space>
      ),
    },
  ];

  const selectedDownloadableCount = orders.filter(
    (order) => selectedRowKeys.includes(order.transportOrderId) && order.viewPdfUrl
  ).length;

  return (
    <div className="order-history-page">
      <div className="order-history-header">
        <div>
          <Typography.Title level={2}>
            <HistoryOutlined /> {t("orderHistory.title", { defaultValue: "Order history" })}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t("orderHistory.subtitle", {
              defaultValue: "Search active, finalized, and archived transport orders without loading the full board.",
            })}
          </Typography.Text>
        </div>
      </div>

      <Card className="order-history-filters">
        <Form form={form} layout="vertical">
          <div className="order-history-filter-grid">
            <Form.Item name="statuses" label={t("orderHistory.status", { defaultValue: "Status" })}>
              <Select
                mode="multiple"
                options={ORDER_STATUSES.map((status) => ({
                  value: status,
                  label: t(`transportManagement.${status.toLowerCase()}`, { defaultValue: status }),
                }))}
              />
            </Form.Item>
            <Form.Item name="dateField" label={t("orderHistory.dateField", { defaultValue: "Date field" })}>
              <Select
                options={DATE_FIELDS.map((field) => ({
                  value: field,
                  label: t(`orderHistory.dateFields.${field}`, { defaultValue: field }),
                }))}
              />
            </Form.Item>
            <Form.Item label={t("orderHistory.dateRange", { defaultValue: "Date range" })}>
              <Space.Compact block>
                <Form.Item name="from" noStyle>
                  <Input type="date" aria-label={t("orderHistory.from", { defaultValue: "From" })} />
                </Form.Item>
                <Form.Item name="to" noStyle>
                  <Input type="date" aria-label={t("orderHistory.to", { defaultValue: "To" })} />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <Form.Item name="driverId" label={t("orderHistory.driver", { defaultValue: "Driver" })}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={driverOptions}
                placeholder={t("orderHistory.anyDriver", { defaultValue: "Any driver" })}
              />
            </Form.Item>
            <Form.Item name="q" label={t("orderHistory.search", { defaultValue: "Search" })}>
              <Input
                allowClear
                placeholder={t("orderHistory.searchPlaceholder", {
                  defaultValue: "Reference, truck, driver, route, eDoc...",
                })}
              />
            </Form.Item>
            <Form.Item name="limit" label={t("orderHistory.pageSize", { defaultValue: "Page size" })}>
              <Select options={PAGE_SIZE_OPTIONS.map((value) => ({ value, label: value }))} />
            </Form.Item>
            <Form.Item name="eDocOnly" valuePropName="checked" className="order-history-checkbox">
              <Checkbox>{t("orderHistory.eDocOnly", { defaultValue: "Only orders with eDoc" })}</Checkbox>
            </Form.Item>
            <Form.Item className="order-history-search-action">
              <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={handleSearch}>
                {t("orderHistory.searchButton", { defaultValue: "Search" })}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Card>

      <Card className="order-history-results">
        <div className="order-history-toolbar">
          <Typography.Text type="secondary">
            {t("orderHistory.resultCount", {
              defaultValue: "{{count}} orders loaded",
              count: orders.length,
            })}
          </Typography.Text>
          <Space>
            <Button
              icon={<DownloadOutlined />}
              disabled={selectedDownloadableCount === 0 || loading}
              loading={isBatchDownloading}
              onClick={handleBatchDownload}
            >
              {t("orderHistory.downloadSelected", {
                defaultValue: "Download selected PDFs",
              })}
              {selectedDownloadableCount > 0 ? ` (${selectedDownloadableCount})` : ""}
            </Button>
            <Button disabled={cursorStack.length === 0 || loading} onClick={handlePreviousPage}>
              {t("orderHistory.previous", { defaultValue: "Previous" })}
            </Button>
            <Button disabled={!nextCursor || loading} onClick={handleNextPage}>
              {t("orderHistory.next", { defaultValue: "Next" })}
            </Button>
          </Space>
        </div>

        <Table
          rowKey="transportOrderId"
          columns={columns}
          dataSource={orders}
          loading={loading}
          pagination={false}
          scroll={{ x: 1180 }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            getCheckboxProps: (order) => ({ disabled: !order.viewPdfUrl }),
          }}
          locale={{
            emptyText: (
              <Empty
                description={t("orderHistory.empty", {
                  defaultValue: "No orders match the current filters.",
                })}
              />
            ),
          }}
        />
      </Card>

      {selectedOrder && (
        <TransportOrderDetailModal
          isOpen={Boolean(selectedOrder)}
          onClose={() => setSelectedOrder(null)}
          transportOrderId={selectedOrder.transportOrderId}
          item={{
            id: selectedOrder.transportOrderId,
            ref: selectedOrder.internalReference || selectedOrder.customerReference || selectedOrder.transportOrderRef,
            status: selectedOrder.status,
            raw: selectedOrder,
          }}
          driverName={selectedOrder.name}
        />
      )}
    </div>
  );
}
